import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useGame } from '../contexts/GameContext';

// Import the image file from the assets directory
import xiImage from '../assets/images/xi.jpg';

// Declare YouTube player type
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

const XiContainer = styled.div`
  position: fixed;
  bottom: 10px;
  left: 10px;
  z-index: 100;
  transition: transform 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const XiHead = styled.img`
  width: 80px;
  height: auto;
  border-radius: 50%;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  border: 2px solid #000000; // Changed from gold to black
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const VideoOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 999;
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: ${props => props.visible ? 'auto' : 'none'};
`;

const VideoContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const YoutubePlayerContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: transparent;
  color: white;
  border: none;
  font-size: 24px;
  cursor: pointer;
  z-index: 1000;
  
  &:hover {
    color: red;
  }
`;

const XiEasterEgg: React.FC = () => {
  const { walletConnected } = useGame();
  const [showVideo, setShowVideo] = useState(false);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const [apiLoaded, setApiLoaded] = useState(false);
  const [isExitingFullscreen, setIsExitingFullscreen] = useState(false);

  // Load YouTube API
  useEffect(() => {
    // Only load the API once
    if (window.YT) {
      setApiLoaded(true);
      return;
    }

    // Create script element for YouTube API
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    
    // Add callback for when API is ready
    window.onYouTubeIframeAPIReady = () => {
      setApiLoaded(true);
    };
    
    // Add script to page
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    
    // Cleanup
    return () => {
      window.onYouTubeIframeAPIReady = () => {};
    };
  }, []);

  // Listen for fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = !!(document.fullscreenElement || 
                            (document as any).webkitFullscreenElement || 
                            (document as any).msFullscreenElement);
      
      // If we're exiting fullscreen on our own, don't do anything
      if (isExitingFullscreen) return;
      
      // If user exits fullscreen other ways (like pressing Escape), close the video
      if (!isFullscreen && showVideo) {
        cleanupAndClose();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, [showVideo, isExitingFullscreen]);

  // Initialize the player when API is loaded and video should be shown
  useEffect(() => {
    if (apiLoaded && showVideo && playerContainerRef.current) {
      // If player already exists, destroy it first
      if (playerRef.current) {
        playerRef.current.destroy();
      }
      
      playerRef.current = new window.YT.Player(playerContainerRef.current, {
        videoId: 'oQwaiw-gUBM',
        playerVars: {
          autoplay: 1,
          controls: 1,
          rel: 0,
          fs: 1,
          playsinline: 1,
          mute: 0
        },
        events: {
          onReady: (event: any) => {
            // Force play when ready
            event.target.playVideo();
            
            // Unmute if needed (to bypass autoplay restrictions)
            setTimeout(() => {
              if (event.target.isMuted()) {
                event.target.unMute();
              }
              // Ensure playing at full volume
              event.target.setVolume(100);
            }, 500);
          }
        }
      });
    }
    
    return () => {
      // Cleanup player on unmount or when video is hidden
      if (!showVideo && playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [apiLoaded, showVideo]);
  
  const handleClick = () => {
    setShowVideo(true);
    
    // Try to request fullscreen mode
    setTimeout(() => {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(err => console.error("Fullscreen error:", err));
      } else if ((elem as any).webkitRequestFullscreen) { /* Safari */
        (elem as any).webkitRequestFullscreen();
      } else if ((elem as any).msRequestFullscreen) { /* IE11 */
        (elem as any).msRequestFullscreen();
      }
    }, 300);
  };
  
  const exitFullscreen = (): Promise<void> => {
    return new Promise<void>((resolve) => {
      setIsExitingFullscreen(true);
      
      if (document.fullscreenElement || 
          (document as any).webkitFullscreenElement || 
          (document as any).msFullscreenElement) {
        
        const onFullscreenExit = () => {
          document.removeEventListener('fullscreenchange', onFullscreenExit);
          document.removeEventListener('webkitfullscreenchange', onFullscreenExit);
          document.removeEventListener('msfullscreenchange', onFullscreenExit);
          
          setTimeout(() => {
            setIsExitingFullscreen(false);
            resolve();
          }, 100);
        };
        
        document.addEventListener('fullscreenchange', onFullscreenExit);
        document.addEventListener('webkitfullscreenchange', onFullscreenExit);
        document.addEventListener('msfullscreenchange', onFullscreenExit);
        
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) { /* Safari */
          (document as any).webkitExitFullscreen();
        } else if ((document as any).msExitFullscreen) { /* IE11 */
          (document as any).msExitFullscreen();
        } else {
          // If we can't exit fullscreen for some reason, still resolve
          setIsExitingFullscreen(false);
          resolve();
        }
      } else {
        // Already not in fullscreen
        setIsExitingFullscreen(false);
        resolve();
      }
    });
  };
  
  const cleanupAndClose = () => {
    // First stop and destroy the player
    if (playerRef.current) {
      try {
        playerRef.current.stopVideo();
        playerRef.current.destroy();
        playerRef.current = null;
      } catch (err) {
        console.error("Error cleaning up player:", err);
      }
    }
    
    // Then set the video to not show
    setShowVideo(false);
  };
  
  const handleClose = async () => {
    // First exit fullscreen, wait for completion
    await exitFullscreen();
    
    // Then clean up the player and hide the video
    cleanupAndClose();
  };
  
  return (
    <>
      {walletConnected && (
        <>
          <XiContainer onClick={handleClick}>
            <XiHead src={xiImage} alt="Easter Egg" />
          </XiContainer>
          
          <VideoOverlay visible={showVideo}>
            <VideoContainer>
              <CloseButton onClick={handleClose}>✕</CloseButton>
              {showVideo && (
                <YoutubePlayerContainer ref={playerContainerRef} />
              )}
            </VideoContainer>
          </VideoOverlay>
        </>
      )}
    </>
  );
};

export default XiEasterEgg; 